import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import { SkuDrawer } from '../components/SkuDrawer';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';
import { fmtMoney, fmtMoney2, fmtInt, fmtPct } from '../lib/fmt';
import { compareValues, paginateRows } from '../lib/table';
import { exportTableAsExcel } from '../lib/export';
import { ABC_COLOR, MARGIN_COLOR, STATUS_COLOR, WF_COLOR, WF_LABEL } from '../lib/colors';
import type { ItemOut, KPIsOut, PaginatedResponse, WorkflowStatus } from '../types/api';

function exportCompradorXLSX(items: ItemOut[]) {
  const headers = ['SKU','Produto','Marca','Qtd sugerida','Participação produto na venda (%)','Valor faturamento','ABC','Status estoque','Saldo SKU','Pedido em aberto','Custo Mercadoria','Margem (%)','Preço-teto','Cobertura (d)','Negociar (%)','Decisão margem','Investimento','Lead time (d)','Pedido mín (R$)','Prazo pgto','Status workflow'];
  const rows = items.map(i => [
    i.sku, i.nome, i.marca??'', i.sugestao_compra,
    Number((i.participacao_venda_pct*100).toFixed(1)),
    Number(i.valor_faturamento),
    i.classe,
    i.status_estoque, i.estoque_total, i.pedidos_aberto,
    Number(i.custo_medio),
    Number((Number(i.margem_pct)*100).toFixed(1)),
    i.preco_teto,
    i.cobertura_dias ?? '',
    Number((i.pct_negociacao*100).toFixed(1)),
    i.classe_margem,
    i.investimento,
    i.regra_lead_time_dias ?? '',
    i.regra_pedido_minimo ?? '',
    i.regra_prazo_pagamento ?? '',
    i.workflow_status,
  ]);
  exportTableAsExcel(`MBC_comprador_${new Date().toISOString().slice(0,10)}.xlsx`, headers, rows);
}


const WF_OPTIONS: { value: WorkflowStatus; dotCls: string }[] = [
  { value: 'pendente',          dotCls: 'bg-slate-400' },
  { value: 'cot_recebida',      dotCls: 'bg-sky-400' },
  { value: 'em_validacao',      dotCls: 'bg-violet-400' },
  { value: 'aprov_comercial',   dotCls: 'bg-emerald-400' },
  { value: 'reprov_comercial',  dotCls: 'bg-rose-400' },
  { value: 'aguard_financeiro', dotCls: 'bg-amber-400' },
  { value: 'aprov_financeiro',  dotCls: 'bg-teal-400' },
  { value: 'pedido',            dotCls: 'bg-blue-400' },
  { value: 'agendado',          dotCls: 'bg-cyan-400' },
  { value: 'receb_parcial',     dotCls: 'bg-indigo-400' },
  { value: 'receb_total',       dotCls: 'bg-green-400' },
];

type CompradorSortField =
  | 'sku' | 'nome' | 'marca' | 'classe' | 'status_estoque' | 'sugestao_compra'
  | 'estoque_total' | 'pedidos_aberto'
  | 'custo_medio' | 'margem_pct' | 'preco_teto' | 'cobertura_dias' | 'pct_negociacao'
  | 'classe_margem' | 'investimento' | 'valor_faturamento' | 'participacao_venda_pct' | 'workflow_status';


export default function Comprador() {
  const qc = useQueryClient();
  const [classeFiltro, setClasseFiltro] = useState('');
  const [marcaFiltro, setMarcaFiltro] = useState('');
  const [abcFiltro, setAbcFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [soComSugestao, setSoComSugestao] = useState(true);
  const [err, setErr] = useState<string|null>(null);
  const [drawerItem, setDrawerItem] = useState<ItemOut | null>(null);
  const [statusMenu, setStatusMenu] = useState<{sku: string; nome: string; current: WorkflowStatus; top: number; left: number; width: number} | null>(null);
  const [focusedStatusIndex, setFocusedStatusIndex] = useState(0);
  const [sortBy, setSortBy] = useState<CompradorSortField>('investimento');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const menuRef = useRef<HTMLDivElement>(null);
  const statusButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const statusOptionRefs = useRef<Record<WorkflowStatus, HTMLButtonElement | null>>({
    pendente: null,
    cot_recebida: null,
    em_validacao: null,
    aprov_comercial: null,
    reprov_comercial: null,
    aguard_financeiro: null,
    aprov_financeiro: null,
    pedido: null,
    agendado: null,
    receb_parcial: null,
    receb_total: null,
    aprovado: null,
    cotado: null,
  });
  const pageSize = 20;

  const closeStatusMenu = (restoreFocus = false) => {
    const sku = statusMenu?.sku;
    setStatusMenu(null);
    if (restoreFocus && sku) {
      requestAnimationFrame(() => {
        statusButtonRefs.current[sku]?.focus();
      });
    }
  };

  const openStatusMenuFromElement = (
    element: HTMLElement,
    item: ItemOut,
    preferredIndex?: number,
  ) => {
    const rect = element.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, 240), Math.min(320, window.innerWidth - 24));
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const top = Math.max(12, Math.min(rect.bottom + 6, window.innerHeight - 260));
    const currentIndex = WF_OPTIONS.findIndex(opt => opt.value === item.workflow_status);
    setFocusedStatusIndex(preferredIndex ?? Math.max(0, currentIndex));
    setStatusMenu({
      sku: item.sku,
      nome: item.nome,
      current: item.workflow_status,
      top,
      left,
      width,
    });
  };

  const openStatusMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: ItemOut,
    preferredIndex?: number,
  ) => {
    event.stopPropagation();
    openStatusMenuFromElement(event.currentTarget, item, preferredIndex);
  };

  useEffect(() => {
    if (!statusMenu) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) closeStatusMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statusMenu]);

  useEffect(() => {
    if (!statusMenu) return;
    const close = () => closeStatusMenu(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [statusMenu]);

  useEffect(() => {
    if (!statusMenu) return;
    const option = WF_OPTIONS[focusedStatusIndex];
    if (!option) return;
    requestAnimationFrame(() => {
      statusOptionRefs.current[option.value]?.focus();
    });
  }, [statusMenu, focusedStatusIndex]);

  const kpisQ = useQuery<KPIsOut>({ queryKey:['kpis'], queryFn:()=>api.get<KPIsOut>('/analysis/kpis').then(r=>r.data) });

  const params = new URLSearchParams({ limit:'2000' });
  if (busca) params.set('busca', busca);
  if (soComSugestao) params.set('com_sugestao','true');

  const { data: itemsResponse, isLoading } = useQuery<PaginatedResponse<ItemOut>>({
    queryKey:['comprador-items', busca, soComSugestao],
    queryFn:()=>api.get<PaginatedResponse<ItemOut>>(`/analysis/items?${params}`).then(r=>r.data),
  });

  useEffect(() => {
    setPage(1);
  }, [busca, soComSugestao, classeFiltro, marcaFiltro, abcFiltro, sortBy, sortDir]);

  const allItems = itemsResponse?.items ?? [];
  const marcas = [...new Set(allItems.map(i => i.marca).filter((marca): marca is string => Boolean(marca)).sort((a, b) => a.localeCompare(b)))];
  const filteredItems = allItems.filter(i => {
    if (classeFiltro && i.classe_margem !== classeFiltro) return false;
    if (marcaFiltro && (i.marca ?? '') !== marcaFiltro) return false;
    if (abcFiltro && i.classe !== abcFiltro) return false;
    return true;
  });
  const getSortValue = (item: ItemOut) => (
    sortBy === 'valor_faturamento' ? Number(item.valor_faturamento) : item[sortBy]
  );
  const sortedItems = [...filteredItems].sort((a, b) => compareValues(getSortValue(a), getSortValue(b), sortDir));
  const items = paginateRows(sortedItems, page, pageSize);

  const bulkStatus = useMutation({
    mutationFn:(payload:{skus:string[];status:WorkflowStatus})=>api.post('/analysis/items/bulk-status',payload),
    onSuccess:()=>{ qc.invalidateQueries({queryKey:['comprador-items']}); },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });

  const k = kpisQ.data;
  const onSort = (field: CompradorSortField) => {
    if (sortBy === field) {
      setSortDir(current => current === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortBy(field);
    setSortDir(
      field === 'sku' || field === 'nome' || field === 'marca' || field === 'classe' || field === 'status_estoque' || field === 'classe_margem' || field === 'workflow_status'
        ? 'asc'
        : 'desc'
    );
  };

  const FILT = [
    { label:'Todos',     value:'',         cls:'bg-mbc-panel2 border border-mbc-line' },
    { label:'Aprovados', value:'Aprovado',  cls:'bg-emerald-500/15 text-emerald-300' },
    { label:'Negociar',  value:'Negociar',  cls:'bg-amber-500/15 text-amber-300' },
    { label:'Bloqueados',value:'Bloqueado', cls:'bg-rose-500/15 text-rose-300' },
  ];

  const selectStatus = (sku: string, status: WorkflowStatus) => {
    bulkStatus.mutate({ skus: [sku], status });
    closeStatusMenu(true);
  };

  const moveStatusFocus = (direction: 1 | -1) => {
    setFocusedStatusIndex(current => {
      const next = current + direction;
      if (next < 0) return WF_OPTIONS.length - 1;
      if (next >= WF_OPTIONS.length) return 0;
      return next;
    });
  };

  return (
    <div className="fade-in">
      <div className="space-y-6">

      {k && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KpiCard label="Sugestões de Compra"  value={fmtInt(k.aprovados+k.negociar+k.bloqueados)} sub="itens a repor"               accent="mbc-orangel" icon="🛒" />
          <KpiCard label="Aprovados p/ cotação" value={fmtInt(k.aprovados)}  sub="margem ≥ 10%"     accent="emerald-300" icon="✅" />
          <KpiCard label="Para negociar"        value={fmtInt(k.negociar)}   sub="margem 0–10%"     accent="amber-300"   icon="🤝" />
          <KpiCard label="Bloqueados"           value={fmtInt(k.bloqueados)} sub="custo acima do teto" accent="rose-300" icon="⛔" />
        </div>
      )}

      <div className="card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="text-white font-bold mr-auto">Sugestões de compra &amp; negociação</h3>
          {FILT.map(b=>(
            <button key={b.value} onClick={()=>setClasseFiltro(b.value)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all ${b.cls} ${classeFiltro===b.value?'ring-2 ring-mbc-orange':''}`}
            >{b.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar SKU ou produto…"
            className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-mbc-mute focus:outline-none focus:border-mbc-orange w-52"/>
          <select
            value={marcaFiltro}
            onChange={e => setMarcaFiltro(e.target.value)}
            className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-mbc-orange min-w-44"
          >
            <option value="">Todas as marcas</option>
            {marcas.map(marca => (
              <option key={marca} value={marca}>{marca}</option>
            ))}
          </select>
          <select
            value={abcFiltro}
            onChange={e => setAbcFiltro(e.target.value)}
            className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-mbc-orange min-w-28"
          >
            <option value="">ABC</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-mbc-soft cursor-pointer">
            <input type="checkbox" checked={soComSugestao} onChange={e=>setSoComSugestao(e.target.checked)} className="accent-mbc-orange"/>
            Só com sugestão
          </label>
          <span className="text-xs text-mbc-mute self-center">{filteredItems.length} itens</span>
          {filteredItems.length > 0 && (
            <button onClick={() => exportCompradorXLSX(filteredItems)} className="ml-auto text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-lg transition-colors">
              📥 Exportar Excel
            </button>
          )}
        </div>

        {err&&<p className="mb-3 text-rose-300 text-sm">{err}</p>}

        <div className="scroller">
          {isLoading ? <div className="p-8 text-center text-mbc-mute">Carregando…</div> : (
            <table className="dt">
              <thead>
                <tr>
                  <SortableTh label="SKU" active={sortBy==='sku'} direction={sortDir} onClick={() => onSort('sku')} style={{width:88}} />
                  <SortableTh label="Produto" active={sortBy==='nome'} direction={sortDir} onClick={() => onSort('nome')} className="text-left" style={{width:220}} />
                  <SortableTh label="Marca" active={sortBy==='marca'} direction={sortDir} onClick={() => onSort('marca')} className="text-left" style={{width:120}} />
                  <SortableTh label="Quantidade sugerida" active={sortBy==='sugestao_compra'} direction={sortDir} onClick={() => onSort('sugestao_compra')} className="num" style={{width:135}} />
                  <SortableTh label="Part. venda" active={sortBy==='participacao_venda_pct'} direction={sortDir} onClick={() => onSort('participacao_venda_pct')} className="num" style={{width:100}} />
                  <SortableTh label="Faturamento" active={sortBy==='valor_faturamento'} direction={sortDir} onClick={() => onSort('valor_faturamento')} className="num" style={{width:120}} />
                  <SortableTh label="ABC" active={sortBy==='classe'} direction={sortDir} onClick={() => onSort('classe')} className="text-center" style={{width:52}} />
                  <SortableTh label="Estoque" active={sortBy==='status_estoque'} direction={sortDir} onClick={() => onSort('status_estoque')} className="text-center" style={{width:95}} />
                  <SortableTh label="Saldo SKU" active={sortBy==='estoque_total'} direction={sortDir} onClick={() => onSort('estoque_total')} className="num" style={{width:90}} />
                  <SortableTh label="Ped. aberto" active={sortBy==='pedidos_aberto'} direction={sortDir} onClick={() => onSort('pedidos_aberto')} className="num" style={{width:95}} />
                  <SortableTh label="Custo Mercadoria" active={sortBy==='custo_medio'} direction={sortDir} onClick={() => onSort('custo_medio')} className="num" style={{width:130}} />
                  <SortableTh label="Margem" active={sortBy==='margem_pct'} direction={sortDir} onClick={() => onSort('margem_pct')} className="num" style={{width:85}} />
                  <SortableTh label="Preço-teto" active={sortBy==='preco_teto'} direction={sortDir} onClick={() => onSort('preco_teto')} className="num" style={{width:115}} />
                  <SortableTh label="Cobertura" active={sortBy==='cobertura_dias'} direction={sortDir} onClick={() => onSort('cobertura_dias')} className="num" style={{width:90}} />
                  <SortableTh label="Negociar" active={sortBy==='pct_negociacao'} direction={sortDir} onClick={() => onSort('pct_negociacao')} className="num" style={{width:90}} />
                  <SortableTh label="Decisão" active={sortBy==='classe_margem'} direction={sortDir} onClick={() => onSort('classe_margem')} className="text-center" style={{width:100}} />
                  <SortableTh label="Investimento" active={sortBy==='investimento'} direction={sortDir} onClick={() => onSort('investimento')} className="num" style={{width:120}} />
                  <th className="text-center" style={{width:100}} title="Lead time, pedido mínimo e prazo de pagamento do fornecedor">Regras</th>
                  <SortableTh label="Status" active={sortBy==='workflow_status'} direction={sortDir} onClick={() => onSort('workflow_status')} className="text-center" style={{width:140}} />
                </tr>
              </thead>
              <tbody>
                {items.map(i=>{
                  const m=Number(i.margem_pct);
                  return (
                    <tr key={i.sku} className="cursor-pointer" onClick={()=>setDrawerItem(i)}>
                      <td><span className="font-mono text-mbc-bluel text-xs">{i.sku}</span></td>
                      <td><span className="truncate inline-block max-w-[200px]" title={i.nome}>{i.nome}</span></td>
                      <td className="text-mbc-mute">{i.marca??'—'}</td>
                      <td className="num">{fmtInt(i.sugestao_compra)}</td>
                      <td className="num">{fmtPct(i.participacao_venda_pct)}</td>
                      <td className="num">{fmtMoney(Number(i.valor_faturamento))}</td>
                      <td className="text-center"><span className={`badge ${ABC_COLOR[i.classe]??''}`}>{i.classe}</span></td>
                      <td className="text-center"><span className={`badge ${STATUS_COLOR[i.status_estoque]??'bg-slate-500/20 text-slate-300'}`}>{i.status_estoque}</span></td>
                      <td className="num">{fmtInt(i.estoque_total)}</td>
                      <td className="num">{fmtInt(i.pedidos_aberto)}</td>
                      <td className="num">{fmtMoney2(Number(i.custo_medio))}</td>
                      <td className="num"><span className={m<0.10?'text-rose-300':'text-emerald-300'}>{fmtPct(m)}</span></td>
                      <td className="num">{fmtMoney2(i.preco_teto)}</td>
                      <td className="num">
                        {i.cobertura_dias != null
                          ? <span className={
                              i.status_estoque === 'Zerado' || i.status_estoque === 'Crítico'
                                ? 'text-rose-300'
                                : i.status_estoque === 'Excesso'
                                ? 'text-amber-300'
                                : 'text-emerald-300'
                            }>{i.cobertura_dias}d</span>
                          : <span className="text-mbc-mute">—</span>}
                      </td>
                      <td className="num">
                        {(i.classe_margem==='Negociar'||i.classe_margem==='Bloqueado')
                          ? <span className="text-amber-300">-{fmtPct(i.pct_negociacao)}</span> : '—'}
                      </td>
                      <td className="text-center"><span className={`badge ${MARGIN_COLOR[i.classe_margem]??'bg-slate-500/20 text-slate-300'}`}>{i.classe_margem}</span></td>
                      <td className="num">{fmtMoney(i.investimento)}</td>
                      <td className="text-center">
                        {i.regra_lead_time_dias != null || i.regra_pedido_minimo != null || i.regra_prazo_pagamento ? (
                          <div className="flex flex-col items-center gap-0.5">
                            {i.regra_lead_time_dias != null && (
                              <span className="badge bg-sky-500/20 text-sky-300 text-[10px]">⏱ {i.regra_lead_time_dias}d</span>
                            )}
                            {i.regra_pedido_minimo != null && (
                              <span className={`badge text-[10px] ${i.investimento < i.regra_pedido_minimo ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-500/15 text-slate-400'}`}
                                title={i.investimento < i.regra_pedido_minimo ? 'Investimento abaixo do pedido mínimo' : 'Pedido mínimo atendido'}>
                                {fmtMoney(i.regra_pedido_minimo)}
                              </span>
                            )}
                            {i.regra_prazo_pagamento && (
                              <span className="badge bg-violet-500/20 text-violet-300 text-[10px]" title="Prazo de pagamento do fornecedor">
                                💳 {i.regra_prazo_pagamento}
                              </span>
                            )}
                          </div>
                        ) : <span className="text-mbc-mute text-xs">—</span>}
                      </td>
                      <td className="text-center" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          ref={el => { statusButtonRefs.current[i.sku] = el; }}
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => openStatusMenu(e, i)}
                          onKeyDown={e => {
                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                              e.preventDefault();
                              e.stopPropagation();
                              openStatusMenuFromElement(
                                e.currentTarget,
                                i,
                                e.key === 'ArrowDown' ? 0 : WF_OPTIONS.length - 1,
                              );
                            }
                          }}
                          aria-haspopup="listbox"
                          aria-expanded={statusMenu?.sku === i.sku}
                          aria-label={`Alterar status do SKU ${i.sku}`}
                          className={`badge status-pill select-none ${WF_COLOR[i.workflow_status]??'bg-slate-500/20 text-slate-300'}`}
                        >
                          {WF_LABEL[i.workflow_status]??i.workflow_status}
                          <span className="caret">▾</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <TablePagination total={filteredItems.length} page={page} pageSize={pageSize} onPageChange={setPage} />
      </div>

      <SkuDrawer
        item={drawerItem}
        onClose={() => setDrawerItem(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['comprador-items'] });
          qc.invalidateQueries({ queryKey: ['kpis'] });
        }}
      />

      </div>

      {statusMenu && createPortal(
        <div
          ref={menuRef}
          className="status-menu"
          style={{ top: statusMenu.top, left: statusMenu.left, width: statusMenu.width }}
          role="listbox"
          aria-label={`Status do SKU ${statusMenu.sku}`}
          tabIndex={-1}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              closeStatusMenu(true);
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              moveStatusFocus(1);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              moveStatusFocus(-1);
              return;
            }
            if (e.key === 'Home') {
              e.preventDefault();
              setFocusedStatusIndex(0);
              return;
            }
            if (e.key === 'End') {
              e.preventDefault();
              setFocusedStatusIndex(WF_OPTIONS.length - 1);
              return;
            }
            if (e.key === 'Tab') {
              closeStatusMenu(false);
              return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const option = WF_OPTIONS[focusedStatusIndex];
              if (option) selectStatus(statusMenu.sku, option.value);
            }
          }}
        >
          <div className="status-menu-head" title={statusMenu.nome}>
            SKU {statusMenu.sku} · {statusMenu.nome.length > 30 ? statusMenu.nome.slice(0, 30) + '…' : statusMenu.nome}
          </div>
          {WF_OPTIONS.map((opt, index) => (
            <button
              key={opt.value}
              type="button"
              ref={el => { statusOptionRefs.current[opt.value] = el; }}
              role="option"
              aria-selected={opt.value === statusMenu.current}
              tabIndex={index === focusedStatusIndex ? 0 : -1}
              className={`status-opt ${opt.value === statusMenu.current ? 'current' : ''} ${index === focusedStatusIndex ? 'focused' : ''}`}
              onMouseEnter={() => setFocusedStatusIndex(index)}
              onClick={() => selectStatus(statusMenu.sku, opt.value)}
            >
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${opt.dotCls}`} />
              <span className="text-sm text-slate-200">{WF_LABEL[opt.value]}</span>
              <span className="check">✓</span>
            </button>
          ))}
        </div>
      , document.body)}
    </div>
  );
}
